import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { SessionList as SharedSessionList } from "@tabipla/components";
import type { SessionResponseDto } from "@tabipla/shared";
import {
  useCreateSession,
  useCompleteSession,
  useReopenSession,
} from "../../../application/hooks/useSessions";

interface SessionListProps {
  groupId: string;
  sessions: SessionResponseDto[];
  onSessionSelect: (sessionId: string) => void;
  canEdit: boolean;
}

export function SessionList({ groupId, sessions, onSessionSelect, canEdit }: SessionListProps) {
  const { t } = useTranslation();
  const createMutation = useCreateSession(groupId);
  const completeMutation = useCompleteSession(groupId);
  const reopenMutation = useReopenSession(groupId);

  const handleCreate = async (name: string) => {
    try {
      await createMutation.mutateAsync({ name });
      toast.success(t("sessions.createSuccess", "精算を作成しました"));
    } catch {
      toast.error(t("sessions.createError", "作成に失敗しました"));
    }
  };

  const handleComplete = async (sessionId: string) => {
    try {
      await completeMutation.mutateAsync(sessionId);
      toast.success(t("sessions.completeSuccess", "精算を完了しました"));
    } catch {
      toast.error(t("sessions.completeError", "完了に失敗しました"));
    }
  };

  const handleReopen = async (sessionId: string) => {
    try {
      await reopenMutation.mutateAsync(sessionId);
      toast.success(t("sessions.reopenSuccess", "精算を再開しました"));
    } catch {
      toast.error(t("sessions.reopenError", "再開に失敗しました"));
    }
  };

  return (
    <SharedSessionList
      sessions={sessions}
      onSessionSelect={onSessionSelect}
      canEdit={canEdit}
      onCreateSession={handleCreate}
      isCreating={createMutation.isPending}
      onCompleteSession={handleComplete}
      isCompleting={completeMutation.isPending}
      onReopenSession={handleReopen}
      isReopening={reopenMutation.isPending}
      // Tabitto-specific text overrides
      title={t("sessions.title", "旅行ごとの精算")}
      emptyDescription={t(
        "sessions.emptyDescription",
        "「新規精算」ボタンから精算を作成しましょう"
      )}
      createDescription={t(
        "sessions.createDescription",
        "旅行ごとに精算を作成すると、出費を分けて管理できます"
      )}
      namePlaceholder={t("sessions.namePlaceholder", "例: 京都旅行 2024/03")}
    />
  );
}
